import { useEffect, useMemo, useRef } from 'react';
import { SearchRequest } from 'app/modules/search/models';

import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

import { identityFunction } from 'app/shared/utils/function';
import { selectHasReadTeamsPermission } from 'app/modules/session/selectors';
import { selectTeamOptions } from 'app/modules/search/selectors';
import { teamsSearch } from 'app/modules/search/actions';
import { useDispatch, useSelector } from 'react-redux';

import {
  U21SelectOptionProps,
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconUsers } from '@u21/tabler-icons';

interface Props {
  getPayload?: (payload: SearchRequest) => SearchRequest;
  selector?: (state: RootState) => U21SelectOptionProps<number>[];
}

export type U21TeamSelectProps<TClearable extends boolean> = DistributiveOmit<
  U21SelectSearchProps<number, TClearable>,
  'options' | 'onSearch'
> &
  Props;

export const U21TeamSelect = <TClearable extends boolean>(
  props: U21TeamSelectProps<TClearable>,
) => {
  const dispatch = useDispatch();
  const {
    getPayload = identityFunction,
    selector = selectTeamOptions,
    ...rest
  } = props;
  const options = useSelector(selector);

  const hasReadTeamsPermission = useSelector(selectHasReadTeamsPermission);

  // assume array if undefined for missing values calculation
  const { value = [] } = props;
  const values = useMemo(
    () => (Array.isArray(value) ? value : [value]),
    [value],
  ) as number[];

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [values]);

  const getPayloadRef = useRef(getPayload);
  getPayloadRef.current = getPayload;
  useEffect(() => {
    if (hasReadTeamsPermission) {
      const missingValues = values.filter(
        (i) => !options.find((j) => j.value === i),
      );
      if (missingValues.length && !calledRef.current) {
        calledRef.current = true;
        dispatch(
          teamsSearch(
            getPayloadRef.current({
              ids: missingValues,
              limit: missingValues.length,
              offset: OFFSET_DEFAULT,
              phrase: '',
            }),
          ),
        );
      }
    }
  }, [dispatch, hasReadTeamsPermission, options, values]);

  if (!hasReadTeamsPermission) {
    return null;
  }

  return (
    <U21SelectSearch
      label="Teams"
      onSearch={(phrase = '') =>
        dispatch(
          teamsSearch(
            getPayload({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase,
            }),
          ),
        )
      }
      options={options}
      startIcon={<IconUsers />}
      {...rest}
    />
  );
};
